import bboxPolygon from '@turf/bbox-polygon';
import circle from '@turf/circle';
import bbox from '@turf/bbox';
import { objectHasKeys } from 'utils/helpers';
import { coordinatesRegex } from 'utils/regexHelpers';
import { createFeatureCollection, createPointFeature } from 'utils/geoJsonHelpers';
import booleanContains from '@turf/boolean-contains';
import { points } from '@turf/helpers';
import center from '@turf/center';

export const createBoxAroundPoint = ({ lat, long }) => {
	const radius = 0.25;
	const options = { units: 'miles' };
	const bufferCircle = circle([long, lat], radius, options);
	const boxBounds = bbox(bufferCircle);
	const square = bboxPolygon(boxBounds);
	const boxCoordinates = square.geometry.coordinates;
	return { boxCoordinates, boxBounds };
};

export const createBoxFeatureCollectionFromPointAndRadius = ({ lat, long, radius }) => {
	const options = { units: 'miles' };
	const bufferCircle = circle([long, lat], radius, options);
	const boxBounds = bbox(bufferCircle);
	const square = bboxPolygon(boxBounds, { id: 'default' });
	return createFeatureCollection({ features: [square], id: 'fromFile' });
};

export const createSourcesStringFromSettingsObject = settings => {
	const urlEncodedEmptyString = '%27%27';
	if (objectHasKeys(settings)) {
		const sources = [];
		for (const [key, value] of Object.entries(settings)) {
			if (value) {
				sources.push(key);
			}
		}
		if (sources.length === 0) {
			return urlEncodedEmptyString;
		}
		return sources.join(',');
	} else {
		//If there are no sources chosen, return an empty string
		return urlEncodedEmptyString;
	}
};

export const toggleMapLayer = ({ currentSettings, layerId }) => {
	const updatedSettings = { ...currentSettings };
	updatedSettings[layerId] = !updatedSettings[layerId];
	return updatedSettings;
};

export const changeMapLayerToggledValue = ({ currentSettings, layerId, value }) => {
	const updatedSettings = { ...currentSettings };
	updatedSettings[layerId] = value;
	return updatedSettings;
};

export const updateLayerSettings = ({ layerToggleList, currentSettings, modifierToggleList }) => {
	const updatedSettings = { ...currentSettings };
	if (layerToggleList.length > 0) {
		for (const layer of layerToggleList) {
			const hasSetting = Object.hasOwnProperty.call(currentSettings, layer.id);
			if (!hasSetting) {
				updatedSettings[layer.id] = true;
			}
		}
	}
	if (modifierToggleList.length > 0) {
		for (const modifier of modifierToggleList) {
			const hasSetting = Object.hasOwnProperty.call(currentSettings, modifier.id);
			if (!hasSetting) {
				updatedSettings[modifier.id] = true;
			}
		}
	}
	return updatedSettings;
};

export const looksLikeCoordinates = value => {
	const matchedValue = value.match(coordinatesRegex);
	return matchedValue ? matchedValue.length > 0 : false;
};

export const convertCoordinatesToMapboxLocation = ({ lat, long, optionType }) => {
	return createPointFeature({
		lat,
		long,
		id: `${long}, ${lat}`,
		center: [long, lat],
		address: '-',
		place_name: `LAT: ${Number(lat).toFixed(6)} || LONG: ${Number(long).toFixed(6)}`,
		optionType
	});
};

export const convertResultBoundsToBoundsArray = resultBounds => {
	if (resultBounds && resultBounds.length > 0) {
		const SW = resultBounds[0];
		const NE = resultBounds[1];
		return SW.lat && SW.lng && NE.lat && NE.lng ? [SW.lng, SW.lat, NE.lng, NE.lat] : null;
	} else {
		return null;
	}
};

export const emptyFeatureCollection = {
	type: 'FeatureCollection',
	features: []
};

export const feetInMile = 5280;

export const convertMilesToFeet = miles => {
	return miles * feetInMile;
};

export const getMapBoundsArray = map => {
	const mapBounds = map.getBounds();
	return [mapBounds._sw.lat, mapBounds._sw.lng, mapBounds._ne.lat, mapBounds._ne.lng];
};

export const convertBoundsQueryParamToBoundsArray = boundsQP => {
	const boundsArray = boundsQP.split(',').map(Number);
	return [
		[boundsArray[1], boundsArray[0]],
		[boundsArray[3], boundsArray[2]]
	];
};

export const getMapBoundingBox = map => {
	const mapBounds = map.getBounds();
	const minX = mapBounds._sw.lng;
	const minY = mapBounds._sw.lat;
	const maxX = mapBounds._ne.lng;
	const maxY = mapBounds._ne.lat;
	return [minX, minY, maxX, maxY];
};

export const getMapboxBoundsFromBoundingBox = boundingBox => {
	//handle if those are undefined
	if (!boundingBox) {
		return null;
	}
	return [
		[boundingBox[0], boundingBox[1]],
		[boundingBox[2], boundingBox[3]]
	];
};

export const getSelectedMapLayerList = ({ allLayers, currentSettings, allModifiers }) => {
	//Also check allModifiers and then create a combined list of layers that are enabled
	const enabledLayers = allLayers.filter(layer => currentSettings[layer?.id]);
	const enabledModifiers =
		allModifiers?.length > 0 ? allModifiers.filter(modifier => currentSettings[modifier?.id]) : [];

	//Create a combined list of layers that are enabled
	const allEnabled = [...enabledLayers, ...enabledModifiers];

	return allEnabled;
};

export const getClickableTileServerMapLayerIds = visibleMapLayers => {
	return visibleMapLayers
		.filter(l => l.preview_properties)
		.map(l => l.id)
		.filter(l => l !== 'user_location' && l !== 'hillshade'); //User location click is handled separately
};

export const filterMapFeaturesByBoundingBox = ({ features, boundingBox }) => {
	const [minX, minY, maxX, maxY] = boundingBox;

	// Lazily create the bounds polygon only if we encounter a non-point feature.
	// For point-heavy collections, this stays O(n) with very low constant cost.
	let mapBoundsPolygon = null;

	return features.filter(feature => {
		if (!feature.geometry) {
			return false;
		}

		// Fast path for Point geometry (most common case for aircraft)
		if (feature.geometry.type === 'Point') {
			const [x, y] = feature.geometry.coordinates;
			return x >= minX && x <= maxX && y >= minY && y <= maxY;
		}

		// Fallback to polygon contains for complex geometries (polygons, lines, etc.)
		if (!mapBoundsPolygon) {
			mapBoundsPolygon = bboxPolygon(boundingBox);
		}
		return booleanContains(mapBoundsPolygon, feature);
	});
};

export const getPolygonCenterFeature = array => {
	const features = points(array);
	return center(features);
};

export const findCenterOfBounds = mapBounds => {
	//Map bounds is this shape: 	[minX, minY, maxX, maxY]
	const boundsPolygon = bboxPolygon(mapBounds);
	const centerFeature = center(boundsPolygon);
	return centerFeature.geometry.coordinates;
};

export const getMapBoundsAsPolygon = map => {
	const mapBounds = map.getBounds();
	const boundingBox = [
		Number(mapBounds._sw.lng.toFixed(6)),
		Number(mapBounds._sw.lat.toFixed(6)),
		Number(mapBounds._ne.lng.toFixed(6)),
		Number(mapBounds._ne.lat.toFixed(6))
	];
	let bboxFeature = bboxPolygon(boundingBox);
	delete bboxFeature.bbox;
	return bboxFeature;
};

export const getPolygonBoundsForFitBounds = polygonFeature => {
	const boundingBox = bbox(polygonFeature);
	return [
		[Number(boundingBox[0].toFixed(6)), Number(boundingBox[1].toFixed(6))], // southwest [lng, lat]
		[Number(boundingBox[2].toFixed(6)), Number(boundingBox[3].toFixed(6))] // northeast [lng, lat]
	];
};

export const addBufferToPixelPoint = ({ point, bufferInPixels }) => {
	const { x, y } = point;
	return [
		[x - bufferInPixels, y - bufferInPixels],
		[x + bufferInPixels, y + bufferInPixels]
	];
};
